import React, { Fragment } from 'react';

import { Duration } from 'wanderu-component-search-result';
import { Text } from 'wanderu-components';
import { localize, i18nMoment as ourMoment } from 'wanderu-i18n';

import {
    useCarrierDataSelector,
    useTripArriveDataSelector,
    useTripDepartDataSelector,
} from '../../hooks';

import styles from './LegInfo.scss';

import type { TFunction } from 'i18next';

interface LegProps {
    cityName: string;
    localizedDateTime: string;
    region: string;
}

interface LegInfoProps {
    i18nMoment: typeof ourMoment,
    numberOfMidnightsPassed: number,
    t: TFunction,
    tripId: string,
    tripModes: string[],
}

const LegInfo = ({
    i18nMoment,
    numberOfMidnightsPassed,
    t,
    tripId,
    tripModes,
}: LegInfoProps) => {
    const origin = useTripDepartDataSelector(tripId);
    const destination = useTripArriveDataSelector(tripId);
    const carrierData = useCarrierDataSelector(tripId);

    const formatLeg = (leg: LegProps) => ({
        location: `${leg.cityName}, ${leg.region}`,
        time: i18nMoment.parseZone(leg.localizedDateTime).format('LT'),
    });

    const originInfo = formatLeg(origin);
    const destinationInfo = formatLeg(destination);

    const originToDestination = t('origin-to-destination', {
        destinationName: destinationInfo.location,
        originName: originInfo.location,
    });

    return (
        <div className={styles.container}>
            <Text variant="text-m" bold>{originToDestination}</Text>
            <Text component="div" variant="text-s">
                <span>
                    <span data-testid="origin-time">{originInfo.time}</span>{' - '}<span data-testid="destination-time">{destinationInfo.time}</span>
                    {numberOfMidnightsPassed > 0 && (
                        <span className={styles.multipleDaysText}>
                            +{numberOfMidnightsPassed}
                        </span>
                    )}
                </span>
                {' '}
                <span>
                    &#40;<Duration className={styles.duration} tripId={tripId} />&#41;
                </span>

                <div>
                    {carrierData.name}
                    {tripModes?.map(type => (
                        <Fragment key={type}> &bull; {t<string>(type)}</Fragment>
                    ))}
                </div>
            </Text>
        </div>
    );
};

export default localize(LegInfo, ['i18nMoment', 't'], 'baseApp');
